/**
 * i18n.ts — lightweight UI string dictionary
 *
 * Usage:
 *   RSC / server:  import { t } from '@/lib/i18n';  →  t('nav_catalog', lang)
 *   Client:        import { useT } from '@/hooks/useT';  →  const t = useT(); t('nav_catalog')
 *
 * Adding a new language:
 *   1. Add the lang code to the `Lang` union.
 *   2. Add a matching object to `dict` (TypeScript will show missing keys).
 *   3. Add the currency mapping to PriceContext if needed.
 */

export type Lang = 'en' | 'ru' | 'uk';

const dict = {
  uk: {
    // -- Navigation ----------------------------------------------------------
    nav_catalog:        'Каталог',
    nav_sales:          'Знижки',
    nav_new:            'Нові',
    nav_preorder:       'Передзамовлення',
    nav_more:           'Подарункові картки',

    // -- Gift Cards ----------------------------------------------------------
    gc_related:         'Схожі товари',
    gc_from:            'від',
    gc_region_label:    'Регіон активації',
    gc_region_selected: 'Обраний регіон',
    gc_denomination:    'Номінал',
    gc_out:             'немає',
    gc_add_cart:        'В кошик',
    gc_buy_now:         'Купити зараз',
    gc_feat_instant:    'Миттєва видача коду на пошту',
    gc_feat_region:     'Активація в регіоні',
    gc_feat_digital:    'Цифровий код',
    gc_feat_pay:        'Оплата криптою та карткою',
    gc_footer:          'Безпечна оплата · Миттєва видача · Гарантія',
    gc_tab_about:       'Опис',
    gc_tab_redeem:      'Як активувати',
    gc_tab_terms:       'Умови',
    gc_tab_reviews:     'Відгуки',
    gc_no_reviews:      'Поки немає відгуків. Будьте першим!',
    gc_toast_oos:       'Немає в наявності',
    gc_toast_cart_err:  'Помилка кошика',
    gc_toast_added:     'Додано в кошик',
    gc_type_voucher:    'Ваучер',
    gc_type_topup:      'Пряме поповнення',
    gc_type_esim:       'eSIM',
    gc_activates_in:    'Активується в країнах',
    gc_subtitle:        'Gift cards, ваучери, поповнення та eSIM — Steam, PlayStation, Apple, Amazon та більше.',
    gc_search_ph:       'Пошук…',
    gc_filters:         'Фільтри',
    gc_sort_title:      'За назвою',
    gc_sort_price:      'Дешевші',
    gc_found:           'Знайдено',
    gc_all:             'Все',
    gc_nothing:         'Нічого не знайдено.',
    gc_type_filter:     'Тип товару',
    gc_country_filter:  'Країна / регіон',
    gc_sort_label:      'Сортування',
    gc_all_types:       'Усі типи',
    gc_all_countries:   'Усі країни',
    gc_reset:           'Скинути',
    gc_show:            'Показати',
    gc_types_voucher:   'Ваучери',
    gc_types_topup:     'Поповнення',
    gc_about_body:      'цифрова подарункова картка з миттєвою видачею коду одразу після підтвердження оплати. Оберіть регіон і номінал, додайте в кошик або натисніть «Купити зараз». Код надійде на пошту та у ваш кабінет.',
    gc_redeem_body:     '1. Скопіюйте отриманий код.\n2. Увійдіть у свій акаунт сервісу.\n3. Відкрийте розділ «Погасити код» / «Redeem» і введіть код.\n4. Баланс зарахується миттєво. Картка діє лише у вибраному регіоні.',
    gc_terms_body:      'Код дійсний лише для облікових записів вибраного регіону. Повернення після відкриття коду неможливе. Переконайтесь, що регіон вашого акаунта збігається з регіоном картки.',

    // -- Header --------------------------------------------------------------
    search_placeholder: 'Пошук ігор, DLC...',
    aria_search:        'Пошук',
    aria_search_clear:  'Очистити',
    aria_theme:         'Тема',
    aria_lang:          'Мова',
    aria_account:       'Особистий кабінет',
    aria_login:         'Увійти',
    aria_menu:          'Меню',
    theme_light:        'Світла тема',
    theme_dark:         'Темна тема',
    nav_aria_label:     'Навігація',

    // -- Footer --------------------------------------------------------------
    footer_all_games:   'Всі ігри',
    footer_all_addons:  'Всі доповнення',
    footer_dlc:         'DLC',
    footer_account:     'Акаунт',
    footer_login:       'Вхід',
    footer_cart:        'Кошик',
    footer_wishlist:    'Вішліст',
    footer_support:     'Підтримка',
    footer_faq:         'FAQ',
    footer_contacts:    'Контакти',
    footer_terms:       'Умови',
    footer_tagline:     'Кращі ціни на ігри та DLC для Xbox і PC.',
    footer_copyright:   '© 2026 GamesSales. Не є офіційним магазином Microsoft.',

    // -- Home sections ------------------------------------------------------─
    section_top_sales:  'Топ знижки',
    section_preorder:   'Передзамовлення',
    section_new:        'Нові надходження',

    // -- Hero slider --------------------------------------------------------─
    hero_more:          'Детальніше',
    hero_prev:          'Попередня',
    hero_next:          'Наступна',
    hero_featured:      'Рекомендовані ігри',
    hero_wishlist_add:  'В вішліст',
    hero_wishlist_remove: 'Видалити з вішліст',

    // -- Product card --------------------------------------------------------
    card_game:          'Гра',
    card_dlc:           'DLC',

    // -- PDP (Product Detail Page) --------------------------------------------
    pdp_about:          'Про гру',
    pdp_collapse:       'Згорнути',
    pdp_expand:         'Показати повністю',
    pdp_wishlist_add:   'В вішліст',
    pdp_wishlist_remove: 'З вішліст',
    pdp_buy:            'Купити',
    pdp_region:         'Регіон',
    pdp_delivery:       'Спосіб доставки',
    pdp_buy_key:        'Купити ключ',
    pdp_buy_gift:       'Купити подарунком',
    pdp_buy_account:    'Отримати акаунт',
    pdp_buy_rent:       'Орендувати',
    pdp_rental_tab:     'Оренда',
    pdp_sale_ended:     'Закінчилась',
    pdp_select_region:  'Виберіть регіон',
    pdp_no_price:       'Ціна уточнюється',
    pdp_not_found:      'Продукт не знайдено',
    pdp_reviews_later:  'Відгуки з\'являться після запуску',
    pdp_no_reviews:     'Поки немає відгуків',
    pdp_reviews_buy:    'Придбайте гру та залиште відгук у',
    pdp_reviews_orders: 'Моїх замовленнях',
    reviews_page_title: 'Відгуки покупців',
    reviews_page_sub:   'Реальні враження від ігор, куплених на GamesSales',
    pdp_tab_details:    'Деталі',
    pdp_tab_more:       'Більше',
    pdp_tab_reviews:    'Відгуки',
    feat_key_1:         'Миттєва доставка на email',
    feat_key_2:         'Активація на Xbox / PC',
    feat_key_3:         'Без регіональних обмежень',
    feat_gift_1:        'Красива подарункова обгортка',
    feat_gift_2:        'Персональне повідомлення',
    feat_gift_3:        'Відправка на будь-який email',
    feat_account_1:     'Готовий Xbox акаунт',
    feat_account_2:     'Гра вже активована',
    feat_account_3:     'Повна підтримка 30 днів',
    feat_rental_1:      'Повний доступ до гри та акаунту',
    feat_rental_2:      'Збереження ігрового прогресу',
    feat_rental_3:      'Підтримка 24/7 протягом оренди',

    // -- Preorder page ------------------------------------------------------─
    preorder_title:     'Очікуються',
    preorder_more:      'Детальніше',
    preorder_search:    'Пошук за назвою...',
    preorder_all:       'Всі',
    preorder_no_results:'Нічого не знайдено',

    // -- Catalog / Sale shell ------------------------------------------------
    catalog_no_results: 'Нічого не знайдено за обраними фільтрами',
    catalog_showing_limit: 'Уточніть фільтри — показано максимум',
    catalog_type_game:  'Ігри',
    catalog_type_dlc:   'DLC',
    catalog_type_all:   'Всі',
    catalog_discount_label: 'Знижка',
    catalog_reset_active: '× Скинути',
    m_filters:  'Фільтри',
    m_sort:     'Сортування',
    m_show:     'Показати',
    m_reset:    'Скинути',
    m_more:     'Додатково',
    m_offer:    'Тип видачі',
    m_region:   'Регіон',
    m_lang:     'Мова',
    m_caps:     'Особливості',
    m_special:  'Спеціальні',
    m_quick:    'Швидкі фільтри',
    filter_year:        'Рік',
    filter_type:        'Тип',
    filter_sort:        'Сортування',
    filter_rating:      'Рейтинг',
    filter_discount:    'Знижки',
    sort_discount_desc: 'Знижки ↓',
    sort_discount_asc:  'Знижки ↑',
    sort_rating_asc:    'Рейтинг ↑',
    sort_new:           'Новинки',

    // -- Cart extended ------------------------------------------------------─
    cart_clear:         'Очистити кошик',
    cart_subtotal:      'Підсумок',
    cart_savings:       'Економія',
    cart_shipping:      'Доставка',
    cart_shipping_free: 'Безкоштовно',
    cart_delivery_instant: 'Миттєва',
    cart_promo:         'Промокод',
    cart_promo_apply:   'Застосувати',
    cart_order_title:   'Ваше замовлення',
    cart_benefits:      'Переваги',
    cart_benefit_1:     'Гарантія повернення коштів',
    cart_benefit_2:     'Миттєва доставка ключів 24/7',
    cart_benefit_3:     'Найкращі ціни по регіонам',
    cart_benefit_4:     '2800+ перевірених транзакцій',
    cart_review_note:   'Перевірте замовлення перед оплатою',
    cart_empty_desc:    'Додайте ігри з каталогу або зі списку бажань',
    cart_go_catalog:    'Каталог ігор',
    cart_go_wishlist:   'Список бажань',
    cart_storage_note:  'Кошик зберігається локально у вашому браузері',
    cart_to_wishlist:   'До бажаних',
    cart_warranty:      'Гарантія',
    cart_feat_rental_save: 'Економія vs купівля',
    cart_period: 'Період',
    cart_vs_buy: 'vs купівля',
    cart_note_base: 'Потрібна базова гра (продається окремо)',
    cart_note_sub: 'Онлайн на консолі потребує Game Pass',
    cart_guarantee:     'Гарантія',
    cart_prices_note:   'Ціни без ПДВ',

    // -- Wishlist ------------------------------------------------------------─
    wishlist_title:     'Список бажань',
    wishlist_desc:      'Збережені ігри та DLC для покупки',
    wishlist_empty:     'Список порожній',
    wishlist_empty_desc:'Додавайте ігри та DLC, щоб не пропустити вигідні пропозиції',
    wishlist_view_catalog: 'Переглянути каталог',
    wishlist_select_all: 'Обрати всі',
    wishlist_add_selected: 'У кошик',
    wishlist_add_all:   'У кошик',
    wishlist_buy_selected: 'Купити',
    wishlist_buy_all:   'Купити все',
    wishlist_share:     'Поділитись',
    wishlist_share_copied: 'Посилання скопійовано!',
    wishlist_share_login:  'Увійдіть, щоб поділитися вішлістом',
    shared_wl_by:          'Вішліст користувача',
    shared_wl_empty:       'Вішліст порожній',
    wishlist_clear:     'Очистити',
    wishlist_clear_confirm: 'Очистити весь список бажань?',
    wishlist_none_for_filter: 'Немає ігор за цим фільтром',
    wishlist_filter_all: 'Всі',
    wishlist_filter_games: 'Ігри',
    wishlist_filter_dlc: 'DLC',
    wishlist_filter_sale: 'Акційні',
    wishlist_sort_date: 'За датою',
    wishlist_sort_cheap: 'Дешевші',
    wishlist_sort_expensive: 'Дорожчі',
    wishlist_sort_sale: 'Зі знижкою',
    wishlist_sort_name: 'За назвою',
    wishlist_added:     'Додано',
    wishlist_info_title: 'Корисна інформація',
    wishlist_info_1:    'Гарантія та підтримка 30 днів',
    wishlist_info_2:    'Миттєва доставка ключів на email',
    wishlist_info_3:    'Найкращі регіональні ціни',
    wishlist_info_4:    'Ціни оновлюються щодня',
    wishlist_last_day:  'Остання доба',
    wishlist_period:    'Період:',
    wishlist_method:    'Спосіб:',
    wishlist_region:    'Регіон:',
    wishlist_delete:    'Видалити',
    wishlist_instant:   'Миттєва доставка',
    wishlist_summary:   'Підсумок',
    wishlist_selected:  'Обрано',
    wishlist_more:      '+ ще',
    wishlist_price_note: 'Ціни включно зі знижками',

    // -- Login extended ------------------------------------------------------─
    login_title:        'Увійти / Зареєструватись',
    login_otp_tab:      'Код на email',
    login_pw_tab:       'Пароль',
    login_email_label:  'EMAIL',
    login_get_code:     'Отримати код',
    login_code_sent:    'Код надіслано на',
    login_enter_code:   'Введіть код',
    login_resend_wait:  'Повторно через',
    login_resend:       'Надіслати новий код',
    login_change_email: 'Змінити email',
    login_success:      'Вхід виконано',
    login_redirecting:  'Перенаправляємо...',
    login_tab_login:    'Вхід',
    login_tab_register: 'Реєстрація',
    login_pw_label:     'ПАРОЛЬ',
    login_pw_placeholder: 'Мінімум 8 символів',
    login_pw_confirm_label: 'ПІДТВЕРДИТИ ПАРОЛЬ',
    login_pw_confirm_placeholder: 'Повторіть пароль',
    login_register_btn: 'Зареєструватись',
    login_tos:          'Входячи, ви погоджуєтесь з умовами використання',
    login_email_placeholder: 'example@email.com',
    login_email_hint:   'Введіть email — надішлемо код підтвердження',

    // -- Account extended ----------------------------------------------------─
    account_orders:     'Замовлення',
    account_rentals:    'Оренди',
    account_wishlist:   'Бажані',
    profile_tab_reviews: 'Відгуки',
    profile_since: 'З',
    acc_client_since: 'Клієнт з',
    acc_orders_word: 'замовлень',
    acc_spent_word: 'витрачено',
    acc_public_profile: 'Профіль публічний',
    acc_logout: 'Вийти',
    set_profile: 'Профіль',
    set_lang: 'Мова інтерфейсу',
    set_save_profile: 'Зберегти профіль',
    set_saving: 'Збереження...',
    set_privacy: 'Приватність',
    set_public_profile: 'Публічний профіль',
    set_public_desc: 'Інші бачать вашу сторінку на сайті',
    set_what_show: 'ЩО ПОКАЗУВАТИ НА ПРОФІЛІ',
    set_show_reviews: 'Відгуки',
    set_show_reviews_desc: 'Список ваших відгуків',
    set_show_purchases: 'Покупки',
    set_show_purchases_desc: 'Бібліотека придбаних ігор',
    set_show_wishlist: 'Список бажаних',
    set_show_wishlist_desc: 'Ваш вішліст',
    set_show_contacts: 'Контакти',
    set_show_contacts_desc: 'Telegram / WhatsApp (з розділу Інформація)',
    set_marketing: 'Маркетингові розсилки',
    set_marketing_desc: 'Акції та знижки на пошту',
    set_save: 'Зберегти',
    set_security: 'Безпека',
    set_no_password: 'Пароль не встановлено — входите через OTP',
    set_current_pw: 'Поточний пароль',
    set_new_pw: 'Новий пароль',
    set_set_pw: 'Встановити пароль',
    set_change_pw: 'Змінити пароль',
    set_min8: 'Мінімум 8 символів',
    set_confirm_pw: 'Підтвердження пароля',
    set_pw_mismatch: 'Паролі не збігаються',
    set_pw_min_err: 'Потрібно мінімум 8 символів',
    set_name_ph: 'Як вас звати?',
    set_add_photo: 'Додати фото',
    set_change_del_photo: 'Змінити або видалити фото',
    set_error: 'Помилка',
    set_saved: 'Збережено',
    set_network_error: 'Мережева помилка',
    profile_tab_purchases: 'Покупки',
    profile_tab_wishlist: 'Бажані',
    account_support:    'Підтримка',
    account_info:       'Інформація',
    account_settings:   'Налаштування',
    account_tab_all:    'Всі',
    account_tab_buy:    'Покупки',
    account_tab_rent:   'Оренда',
    account_status_all:       'Усі статуси',
    account_status_pending:   'Очікують оплати',
    account_status_completed: 'Виконані',
    account_status_cancelled: 'Скасовані',
    order_pay_now:            'Перейти до оплати',
    order_verifying:          'Перевіряємо вашу оплату',
    order_pay_other:          'Оплата не пройшла? Обрати інший спосіб',
    order_open_full:          'Відкрити замовлення',
    order_choose_method:      'Виберіть спосіб оплати',
    account_no_orders:  'Замовлень ще немає',
    account_no_orders_desc: 'Зробіть перше замовлення в каталозі',
    account_go_catalog: 'До каталогу',
    account_no_orders_filter: 'Немає замовлень у цій категорії',
    account_ms_account: 'Microsoft акаунт',
    account_ms_email:   'Microsoft Email',
    account_ms_note:    'Буде використовуватись автоматично при наступних покупках',
    account_contacts:   'Контакти',
    account_saving:     'Збереження...',
    account_saved:      'Збережено',
    account_save_contacts: 'Зберегти контакти',
    account_net_error:  'Мережева помилка',
    account_tg_placeholder: 'username без @',
    account_wa_placeholder: '+380...',

    // -- Releases page --------------------------------------------------------
    releases_title:     'РЕЛІЗИ',
    releases_desc:      'Оновлюється щодня',

    // -- Order / payment ----------------------------------------------------─
    order_paid_title:        'Оплата підтверджена',
    order_step_placed:       'Замовлення',
    order_step_paid:         'Оплата',
    order_step_delivered:    'Видача',
    order_step_preparing:    'Підготовка',
    order_step_done:         'Готово',
    order_step_status_done:    'Завершено',
    order_step_status_failed:  'Не вдалось',
    order_step_status_waiting: 'Очікує оплати',
    order_step_status_active:  'В обробці',
    order_step_status_pending: 'Очікує',
    order_receipt:           'Чек замовлення',
    order_date_label:        'Дата',
    order_total_label:       'Разом',
    order_failed_title:      'Оплата не пройшла',
    order_partial_title:     'Часткова оплата',
    order_waiting_title:     'Очікування оплати',
    order_paid_desc:         'Менеджер надішле дані доступу на email протягом 1–24 год.',
    order_done_title:        'Замовлення виконано',
    order_done_desc:         'Дякуємо за покупку!',
    order_failed_desc:       'Транзакцію скасовано або минув термін.',
    order_partial_desc:      'Отримано неповну суму. Доплатіть решту на ту саму адресу.',
    order_waiting_desc:      'Перевіряємо транзакцію. Сторінка оновлюється автоматично.',
    order_auto_refresh:      'Автооновлення кожні 5 сек',
    order_not_found:         'Замовлення не знайдено або у вас немає доступу',
    order_pay_heleket:       'Доплатити на Heleket',
    order_partial_banner:    'Часткова оплата — очікується доплата',
    order_received_label:    'ОТРИМАНО',
    order_remaining_label:   'ЗАЛИШИЛОСЬ',
    order_received_info:     'Отримано:',
    order_network_label:     'мережа:',
    order_number_label:      'ЗАМОВЛЕННЯ',
    order_amount_label:      'СУМА',
    order_completed:         'Завершено',
    order_account_note:      'Менеджер надішле логін+пароль акаунту після підтвердження оплати.',
    order_rental_until:      'до',
    order_key_note:          'Ключ активації надсилається на email після підтвердження оплати.',
    order_all_keys:          'Мої замовлення',
    order_back_to_orders:    'До замовлень',
    order_page_title:        'Замовлення',
    order_badge_done:        'Виконано',
    order_badge_paid:        'Оплачено',
    order_badge_failed:      'Оплата не пройшла',
    order_badge_cancelled:   'Скасовано',
    order_badge_pending:     'Очікує оплати',
    order_items_title:       'Товари в замовленні',
    order_items_label:       'Товари',
    order_fee_label:         'Комісія платіжної системи',
    order_details_title:     'Деталі замовлення',
    order_recipient_email_label: 'Email отримувача',
    order_method_label:      'Спосіб оплати',
    order_method_pending:    'Ще не обрано',
    order_method_paid_fallback: 'Оплачено',
    order_method_heleket_tagline: 'крипта/картки',
    order_countdown_label: 'Оплатіть протягом',
    order_countdown_expired_label: 'Час вийшов',
    order_status_label:      'Статус',
    order_support_title:     'Потрібна допомога?',
    order_support_desc:      'Якщо виникли питання щодо замовлення — напишіть нам.',
    order_support_cta:       'Звʼязатися з підтримкою',
    order_fulfillment_ask_email_title: 'Вкажіть Microsoft-акаунт',
    order_fulfillment_ask_email_desc:  'На цю пошту ми надішлемо запрошення в сімейну групу Xbox — одну на все замовлення.',
    order_fulfillment_submit_email:    'Продовжити',
    order_fulfillment_email_invalid:   'Введіть коректний email',
    order_fulfillment_invite_title:    'Запрошення готове',
    order_fulfillment_invite_desc:     'Прийміть запрошення в сімейну групу Xbox, потім натисніть «Я вступив».',
    order_fulfillment_family_check_warning: 'Перш ніж вступати — перевірте на account.microsoft.com/family, що ви ще НЕ перебуваєте в іншій сімейній групі Xbox (одночасно можна бути лише в одній). Якщо перебуваєте — спочатку вийдіть звідти.',
    order_fulfillment_family_delivered_note: 'Гру видано — вона вже у вашій бібліотеці (Особисті ігри). Ми вийшли з сімейної групи — це не впливає на гру.',
    order_fulfillment_open_invite:     'Відкрити запрошення',
    order_fulfillment_confirm_joined:  'Я вступив',
    order_fulfillment_ownership_error: 'Не вдалося підтвердити. Напишіть у підтримку — ми перевіримо вручну.',
    order_fulfillment_change_email:    'змінити',
    order_fulfillment_save_email:      'Зберегти',
    order_fulfillment_in_progress:     'Обробляємо видачу',
    order_fulfillment_in_progress_desc: 'Це займає деякий час — сторінка оновиться автоматично.',
    order_fulfillment_needs_attention: 'Потрібна увага оператора',
    order_fulfillment_support_hint:    'Ми вже бачимо цю ситуацію і займаємось вручну — напишіть у підтримку, якщо хочете деталі.',
    order_pay_action_title:  'Оплатіть замовлення',
    order_pay_action_desc:   'Ми зарезервували товари. Після оплати почнемо обробку замовлення.',
    order_partial_instruction: 'Heleket отримав вашу транзакцію, але сума не збіглась. Доплатіть решту на ту саму адресу через Heleket.',
    nav_my_orders:           'Мої замовлення',

    // -- Wishlist dates ------------------------------------------------------
    date_today:         'Сьогодні',
    date_yesterday:     'Вчора',
    date_days_ago:      'дні тому',
    date_week_ago:      '1 тиждень тому',
    date_weeks_ago:     'тижні тому',
    date_sale_ends_d:   'до кінця акції',
    date_sale_ends_h:   'год до кінця',
    date_sale_hours:    'год',
    date_sale_days:     'д',
    date_sale_min:      'хв',

    // -- Catalog / Sort labels ------------------------------------------------─
    sort_popular:       'Популярне',
    filter_all_genres:  'Всі жанри',
    filter_close:       'закрити',
    genre_platformer:   'Платформер',
    genre_family:       'Сімейні',
    genre_music:        'Музика',
    catalog_items_count: 'товарів',
    catalog_optimized:  'Оптимізовано для Series X|S',

    // -- Sort labels --------------------------------------------------------─
    sort_date_asc:      'Дата ↑',
    sort_date_desc:     'Дата ↓',
    sort_price_asc:     'Ціна ↑',
    sort_price_desc:    'Ціна ↓',
    sort_rating_desc:   'Рейтинг ↓',
    sort_name_asc:      'Назва ↑',

    // -- Catalog / filters --------------------------------------------------─
    filter_platform:    'Платформа',
    filter_genre:       'Жанр',
    filter_price:       'Ціна',
    filter_features:    'Особливості',
    filter_reset:       'Скинути',
    filter_studio:      'Студія...',
    filter_lang:        'Мова',
    filter_region:      'Регіон',
    filter_gold:        'Gold Required',
    genre_action:       'Екшн',
    genre_shooter:      'Шутер',
    genre_rpg:          'RPG',
    genre_racing:       'Гонки',
    genre_sports:       'Спорт',
    genre_fighting:     'Файтинг',
    genre_simulation:   'Симулятор',
    genre_strategy:     'Стратегія',
    genre_puzzle:       'Головоломка',
    genre_horror:       'Жахи',
    genre_adventure:    'Пригоди',
    platform_all:       'Всі',
    filter_genres:      'Жанри',
    filter_aria_genre:  'Фільтр за жанром',
    filter_features_reset: 'Скинути особливості',
    filter_year_reset:  'Скинути роки',
    filter_feat_suffix: 'особл.',
    filter_year_suffix: 'рр.',
    catalog_showing:    'Показано',
    catalog_of:         'з',
    catalog_limit_hint: 'уточніть фільтри',

    // -- Delivery types ------------------------------------------------------
    delivery_key:       'Ключ',
    delivery_gift:      'Подарунок',
    delivery_account:   'Акаунт',
    delivery_rental:    'Оренда',

    // -- Cart ----------------------------------------------------------------
    cart_title:         'Кошик',
    checkout_title:        'Оформлення замовлення',
    checkout_back:         'Назад до кошика',
    checkout_recipient:    'Email отримувача',
    checkout_recipient_hint:'Сюди надішлемо ключ / дані після оплати',
    checkout_use_account_email: 'Використати email мого акаунту',
    checkout_email_invalid:'Вкажіть коректний email',
    checkout_method:       'Спосіб оплати',
    checkout_no_methods:   'Немає доступних методів оплати',
    checkout_fee:          'Комісія методу',
    checkout_items:        'Товарів',
    checkout_total:        'Разом',
    checkout_pay:          'Оплатити',
    checkout_creating:     'Створення рахунку…',
    checkout_delivery_title:'Доставка після оплати',
    checkout_delivery_note:'Надішлемо дані на ваш email протягом 1–24 год.',
    checkout_delivery_note_rental:'Надішлемо дані акаунту (логін+пароль) протягом 1–24 год.',
    checkout_secure:       'Захищена транзакція',
    checkout_rental_duration:'Тривалість оренди',
    checkout_failed_title: 'Оплата не пройшла',
    checkout_failed_desc:  'Кошти не списано. Спробуйте ще раз або оберіть інший спосіб оплати.',
    checkout_failed_retry: 'Повернутись до кошика',
    checkout_failed_orders:'Мої замовлення',
    cart_empty:         'Кошик порожній',
    cart_total:         'Разом',
    cart_checkout:      'Оформити замовлення',
    cart_added:         'Додано',
    cart_method:        'Метод',
    cart_days:          'дн',
    cart_remove:        'Видалити',

    // -- Orders / Account ----------------------------------------------------
    order_status_pending:    'Очікує оплати',
    order_status_paid:       'Оплачено',
    order_status_failed:     'Не оплачено',
    order_status_refunded:   'Повернуто',
    fulfill_pending:         'Очікується',
    fulfill_processing:      'В обробці',
    fulfill_partial:         'Частково',
    fulfill_delivered:       'Доставлено',
    fulfill_failed:          'Помилка',
    key_show:                'Показати',
    key_hide:                'Сховати ключ',
    key_copy:                'Скопіювати',
    order_account_login:    'Логін',
    order_account_password: 'Пароль',
    key_copied:              'Скопійовано',
    key_waiting:             'очікуємо...',
    key_open_game:           'Відкрити сторінку гри',
    rental_days_suffix:      'дн.',

    // -- Reviews ------------------------------------------------------------─
    review_leave:       'Залишити відгук',
    review_edit:        'Редагування відгуку',
    review_edit_btn:    'Редагувати',
    review_rating:      'Оцінка',
    review_placeholder: "Розкажіть про враження (необов'язково)...",
    review_save:        'Опублікувати',
    review_update:      'Оновити',
    review_saving:      'Збереження...',
    review_cancel:      'Скасувати',
    review_thanks:      'Дякуємо за відгук!',
    review_updated:     'Відгук оновлено!',
    review_loading:     'Завантаження...',
    review_delete_confirm: 'Видалити ваш відгук?',
    review_label:       'Відгук',
    rating_1: 'Погано', rating_2: 'Не дуже', rating_3: 'Нормально',
    rating_4: 'Добре',  rating_5: 'Відмінно',

    // -- Support / Tickets --------------------------------------------------─
    support_title:      'Підтримка',
    support_subtitle:   'Ваші звернення до служби підтримки',
    support_new:        'Нове звернення',
    support_empty:      'Звернень немає',
    support_empty_desc: "Тут з'являться ваші звернення. Виникли питання — напишіть нам.",
    ticket_open:        'Відкрито',
    ticket_in_progress: 'В обробці',
    ticket_resolved:    'Вирішено',
    ticket_closed:      'Закрито',
    ticket_subject:     'Тема',
    ticket_body:        'Повідомлення',
    ticket_placeholder_subject: 'Коротко опишіть проблему',
    ticket_placeholder_body:    'Детально опишіть вашу проблему...',
    ticket_submit:      'Відправити',
    ticket_submitting:  'Відправка...',
    ticket_cancel:      'Скасувати',
    ticket_new_reply:   'Нова відповідь підтримки',
    ticket_order:       'Замовлення',
    support_load_error: 'Не вдалося завантажити.',
    support_retry:      'Спробувати знову',
    td_all_tickets:     'Всі звернення',
    td_load_error:      'Не вдалося завантажити звернення.',
    td_await:           'Очікуємо відповіді від підтримки',
    td_support:         'Підтримка',
    td_you:             'Ви',
    td_placeholder:     'Написати повідомлення... (Ctrl+Enter для надсилання)',
    td_send:            'Надіслати',
    td_closed:          'Звернення закрите.',
    td_order_details:   'Деталі замовлення',
    td_goto_orders:     'Перейти до замовлень',
    td_transaction:     'Транзакція',
    td_send_error:      'Помилка надсилання.',
    td_send_error_retry:'Помилка надсилання. Спробуйте ще раз.',

    // -- Login / Auth --------------------------------------------------------─
    login_error_rate:   'Забагато спроб. Зачекайте 10 хвилин.',
    login_error_send:   'Помилка надсилання.',
    login_error_code:   'Невірний код.',
    login_error_fields: 'Заповніть всі поля.',
    login_error_login:  'Помилка входу.',
    login_error_min_pw: 'Пароль мінімум 8 символів.',
    login_error_pw_match: 'Паролі не збігаються.',
    login_error_reg:    'Помилка реєстрації.',

    // -- Common --------------------------------------------------------------─
    common_loading:     'Завантаження...',
    common_error:       'Помилка',
    common_retry:       'Спробувати знову',
    common_more:        'Ще',
    common_close:       'Закрити',
    common_save:        'Зберегти',
    common_cancel:      'Скасувати',
    common_confirm:     'Підтвердити',
    common_see_all:     'Дивитись всі',
    common_load_more:   'Завантажити ще',
    common_not_found:   'Нічого не знайдено',
    nf_title:           'Сторінку не знайдено',
    nf_desc:            'Схоже, ця сторінка загубилась у космосі. Перевірте адресу або поверніться до каталогу.',
    nf_home:            'На головну',
    nf_catalog:         'До каталогу',
    err_title:          'Щось пішло не так',
    err_desc:           'Сталася непередбачена помилка. Спробуйте ще раз — зазвичай це допомагає.',
    err_retry:          'Спробувати ще раз',
    common_of:          'з',
    common_all:         'Всі',
    common_reset_filters: 'Скинути фільтри',
    common_remaining:   'залишилось',
    sort_aria:          'Сортування',
    common_game_count_1: 'гра',
    common_game_count_2_4: 'гри',
    common_game_count_many: 'ігор',
    studio_dev:         'розробник',
    studio_pub:         'видавець',
    studio_both:        'dev+pub',

    // -- PDP extra ------------------------------------------------------------─
    pdp_added:          'Додано!',
    pdp_activation_region: 'Регіон активації',
    pdp_selected_region:   'Обраний регіон',
    pdp_regions_count:     'регіонів',
    pdp_vpn_note:          'VPN може знадобитись для активації. Інструкція надається після покупки.',
    pdp_delivery_instant:  'Є в наявності — Миттєво',
    pdp_delivery_24h:      'До 24 год',
    pdp_delivery_in:       'Є в:',
    pdp_footer_buy:        'Захищена оплата · Миттєва доставка · Гарантія',
    pdp_price_label:       'Ціна',
    pdp_unavailable:       'Недоступно',
    pdp_unavailable_content: 'Цей вміст зараз недоступний для придбання.',
    pdp_free_game_note:    'Ця гра безкоштовна. Придбання через наш магазин недоступне.',
    pdp_popular:           'Популярне',
    pdp_rental_duration:   'Тривалість оренди',
    pdp_rental_access:     'Повний доступ до акаунту та гри',
    pdp_per_day:           '/день',
    pdp_rental_for:        'за',
    pdp_rental_days_long:  'днів',
    pdp_footer_rent:       'Безпечна оренда · Гарантія доступу · Підтримка',
    pdp_sale_active:       'Акція діє ще:',
    pdp_in_bundle:         'У цьому бандлі',
    pdp_included_in:       'Входить до',
    pdp_addons_for:        'Доповнення для гри',
    pdp_compare_editions:  'Порівняти версії',
    pdp_works_with:        'Працює з',
    pdp_people_like:       'Схожі ігри',
    pdp_capabilities:      'Можливості',
    pdp_tech_info:         'Технічна інформація',
    pdp_lang_support:      'Підтримка мов',
    pdp_lang_count:        'мов',
    pdp_lang_col_lang:     'Мова',
    pdp_lang_col_audio:    'Звук',
    pdp_lang_col_subs:     'Субт.',
    pdp_tech_developer:    'Розробник',
    pdp_tech_publisher:    'Видавець',
    pdp_tech_release_year: 'Рік виходу',
    pdp_tech_release_date: 'Дата виходу',
    pdp_coming_soon:       'Незабаром',
    pdp_soon:              'Скоро',
    pdp_coming_soon_date:  'Реліз {date}',
    pdp_coming_soon_desc:  'Гра ще не вийшла. Ціна та передзамовлення зʼявляться ближче до релізу.',
    pdp_tech_size:         'Розмір',
    pdp_trust_guarantee:   'Гарантія',
    pdp_trust_support:     'Підтримка',
    pdp_trust_sold:        'Проданих',

    // -- Offer filter ----------------------------------------------------------
    offer_key:          'Ключ',
    offer_gift:         'Гіфт',
    offer_account:      'Акаунт',
    offer_rental:       'Оренда',
    offer_type_title:   'Тип доставки',
    offer_region_any:   'Будь-який',
    offer_na:           'н/д',
    offer_unavail_for:  'Недоступно для',

    // -- Language filter ------------------------------------------------------
    filter_lang_interface: 'Інтерфейс',
    filter_lang_audio:  'Аудіо',
    filter_lang_subs:   'Субтитри',
    filter_lang_reset:  'Скинути мову',

    // -- Preorder hero --------------------------------------------------------
    preorder_next_release: 'НАСТУПНИЙ РЕЛІЗ',
    preorder_preorder:  'Передзамовити',
    preorder_empty_title: 'Передзамовлень немає',
    preorder_empty_desc: "З'являться тут автоматично",

    // -- Countdown ------------------------------------------------------------
    cd_days:            'дн',
    cd_hours:           'год',
    cd_minutes:         'хв',
    cd_seconds:         'сек',
    cd_released:        'Вже вийшов',

    // -- Sale page ------------------------------------------------------------
    sale_title:         'Знижки',
    sale_games_count:   'ігор зі знижкою',
    sale_updated_daily: 'оновлюється щодня',
  },

  ru: {
    nav_catalog:        'Каталог',
    nav_sales:          'Скидки',
    nav_new:            'Новинки',
    nav_preorder:       'Предзаказы',
    nav_more:           'Подарочные карты',

    // -- Gift Cards ----------------------------------------------------------
    gc_related:         'Похожие товары',
    gc_from:            'от',
    gc_region_label:    'Регион активации',
    gc_region_selected: 'Выбранный регион',
    gc_denomination:    'Номинал',
    gc_out:             'нет',
    gc_add_cart:        'В корзину',
    gc_buy_now:         'Купить сейчас',
    gc_feat_instant:    'Мгновенная выдача кода на почту',
    gc_feat_region:     'Активация в регионе',
    gc_feat_digital:    'Цифровой код',
    gc_feat_pay:        'Оплата криптой и картой',
    gc_footer:          'Безопасная оплата · Мгновенная выдача · Гарантия',
    gc_tab_about:       'Описание',
    gc_tab_redeem:      'Как активировать',
    gc_tab_terms:       'Условия',
    gc_tab_reviews:     'Отзывы',
    gc_no_reviews:      'Пока нет отзывов. Будьте первым!',
    gc_toast_oos:       'Нет в наличии',
    gc_toast_cart_err:  'Ошибка корзины',
    gc_toast_added:     'Добавлено в корзину',
    gc_type_voucher:    'Ваучер',
    gc_type_topup:      'Прямое пополнение',
    gc_type_esim:       'eSIM',
    gc_activates_in:    'Активируется в странах',
    gc_subtitle:        'Gift cards, ваучеры, пополнения и eSIM — Steam, PlayStation, Apple, Amazon и другое.',
    gc_search_ph:       'Поиск…',
    gc_filters:         'Фильтры',
    gc_sort_title:      'По названию',
    gc_sort_price:      'Дешевле',
    gc_found:           'Найдено',
    gc_all:             'Все',
    gc_nothing:         'Ничего не найдено.',
    gc_type_filter:     'Тип товара',
    gc_country_filter:  'Страна / регион',
    gc_sort_label:      'Сортировка',
    gc_all_types:       'Все типы',
    gc_all_countries:   'Все страны',
    gc_reset:           'Сбросить',
    gc_show:            'Показать',
    gc_types_voucher:   'Ваучеры',
    gc_types_topup:     'Пополнение',
    gc_about_body:      'цифровая подарочная карта с мгновенной выдачей кода сразу после подтверждения оплаты. Выберите регион и номинал, добавьте в корзину или нажмите «Купить сейчас». Код придёт на почту и в ваш кабинет.',
    gc_redeem_body:     '1. Скопируйте полученный код.\n2. Войдите в свой аккаунт сервиса.\n3. Откройте раздел «Погасить код» / «Redeem» и введите код.\n4. Баланс зачислится мгновенно. Карта действует только в выбранном регионе.',
    gc_terms_body:      'Код действителен только для учётных записей выбранного региона. Возврат после открытия кода невозможен. Убедитесь, что регион вашего аккаунта совпадает с регионом карты.',
    search_placeholder: 'Поиск игр, DLC...',
    aria_search:        'Поиск',
    aria_search_clear:  'Очистить',
    aria_theme:         'Тема',
    aria_lang:          'Язык',
    aria_account:       'Личный кабинет',
    aria_login:         'Войти',
    aria_menu:          'Меню',
    theme_light:        'Светлая тема',
    theme_dark:         'Тёмная тема',
    nav_aria_label:     'Навигация',
    footer_all_games:   'Все игры',
    footer_all_addons:  'Все дополнения',
    footer_dlc:         'DLC',
    footer_account:     'Аккаунт',
    footer_login:       'Войти',
    footer_cart:        'Корзина',
    footer_wishlist:    'Вишлист',
    footer_support:     'Поддержка',
    footer_faq:         'FAQ',
    footer_contacts:    'Контакты',
    footer_terms:       'Условия',
    footer_tagline:     'Лучшие цены на игры и DLC для Xbox и PC.',
    footer_copyright:   '© 2026 GamesSales. Не является официальным магазином Microsoft.',
    section_top_sales:  'Топ скидок',
    section_preorder:   'Предзаказы',
    section_new:        'Новинки',
    hero_more:          'Подробнее',
    hero_prev:          'Предыдущий',
    hero_next:          'Следующий',
    hero_featured:      'Рекомендуемые игры',
    hero_wishlist_add:  'В вишлист',
    hero_wishlist_remove: 'Убрать из вишлиста',
    card_game:          'Игра',
    card_dlc:           'DLC',
    pdp_about:          'Об игре',
    pdp_collapse:       'Свернуть',
    pdp_expand:         'Показать полностью',
    pdp_wishlist_add:   'В вишлист',
    pdp_wishlist_remove: 'Убрать из вишлиста',
    pdp_buy:            'Купить',
    pdp_region:         'Регион',
    pdp_delivery:       'Способ доставки',
    pdp_buy_key:        'Купить ключ',
    pdp_buy_gift:       'Купить подарком',
    pdp_buy_account:    'Получить аккаунт',
    pdp_buy_rent:       'Арендовать',
    pdp_rental_tab:     'Аренда',
    pdp_sale_ended:     'Закончилась',
    pdp_select_region:  'Выберите регион',
    pdp_no_price:       'Цена уточняется',
    pdp_not_found:      'Продукт не найден',
    pdp_reviews_later:  'Отзывы появятся после запуска',
    pdp_no_reviews:     'Пока нет отзывов',
    pdp_reviews_buy:    'Купите игру и оставьте отзыв в',
    pdp_reviews_orders: 'Моих заказах',
    reviews_page_title: 'Отзывы покупателей',
    reviews_page_sub:   'Реальные впечатления от игр, купленных на GamesSales',
    pdp_tab_details:    'Детали',
    pdp_tab_more:       'Ещё',
    pdp_tab_reviews:    'Отзывы',
    feat_key_1:         'Мгновенная доставка на email',
    feat_key_2:         'Активация на Xbox / PC',
    feat_key_3:         'Без региональных ограничений',
    feat_gift_1:        'Красивая подарочная обёртка',
    feat_gift_2:        'Персональное сообщение',
    feat_gift_3:        'Отправка на любой email',
    feat_account_1:     'Готовый Xbox аккаунт',
    feat_account_2:     'Игра уже активирована',
    feat_account_3:     'Полная поддержка 30 дней',
    feat_rental_1:      'Полный доступ к игре и аккаунту',
    feat_rental_2:      'Сохранение игрового прогресса',
    feat_rental_3:      'Поддержка 24/7 во время аренды',
    catalog_no_results: 'Ничего не найдено по выбранным фильтрам',
    catalog_showing_limit: 'Уточните фильтры — показан максимум',
    catalog_type_game:  'Игры',
    catalog_type_dlc:   'DLC',
    catalog_type_all:   'Все',
    catalog_discount_label: 'Скидка',
    catalog_reset_active: '× Сбросить',
    m_filters:  'Фильтры',
    m_sort:     'Сортировка',
    m_show:     'Показать',
    m_reset:    'Сбросить',
    m_more:     'Дополнительно',
    m_offer:    'Тип выдачи',
    m_region:   'Регион',
    m_lang:     'Язык',
    m_caps:     'Особенности',
    m_special:  'Специальные',
    m_quick:    'Быстрые фильтры',
    filter_year:        'Год',
    filter_type:        'Тип',
    filter_sort:        'Сортировка',
    filter_rating:      'Рейтинг',
    filter_discount:    'Скидки',
    sort_discount_desc: 'Скидки ↓',
    sort_discount_asc:  'Скидки ↑',
    sort_rating_asc:    'Рейтинг ↑',
    sort_new:           'Новинки',
    cart_clear:         'Очистить корзину',
    cart_subtotal:      'Итого',
    cart_savings:       'Экономия',
    cart_shipping:      'Доставка',
    cart_shipping_free: 'Бесплатно',
    cart_delivery_instant: 'Мгновенно',
    cart_promo:         'Промокод',
    cart_promo_apply:   'Применить',
    cart_order_title:   'Ваш заказ',
    cart_benefits:      'Преимущества',
    cart_benefit_1:     'Гарантия возврата средств',
    cart_benefit_2:     'Мгновенная доставка ключей 24/7',
    cart_benefit_3:     'Лучшие цены по регионам',
    cart_benefit_4:     '2800+ проверенных транзакций',
    cart_review_note:   'Проверьте заказ перед оплатой',
    cart_empty_desc:    'Добавьте игры из каталога или из списка желаний',
    cart_go_catalog:    'Каталог игр',
    cart_go_wishlist:   'Список желаний',
    cart_storage_note:  'Корзина хранится локально в вашем браузере',
    cart_to_wishlist:   'В желаемое',
    cart_warranty:      'Гарантия',
    cart_feat_rental_save: 'Экономия vs покупка',
    cart_period: 'Период',
    cart_vs_buy: 'vs покупка',
    cart_note_base: 'Требуется базовая игра (продаётся отдельно)',
    cart_note_sub: 'Онлайн на консоли требует Game Pass',
    cart_guarantee:     'Гарантия',
    cart_prices_note:   'Цены без НДС',
    wishlist_title:     'Список желаний',
    wishlist_desc:      'Сохранённые игры и DLC для покупки',
    wishlist_empty:     'Список пуст',
    wishlist_empty_desc:'Добавляйте игры и DLC, чтобы не пропустить выгодные предложения',
    wishlist_view_catalog: 'Перейти в каталог',
    wishlist_select_all: 'Выбрать все',
    wishlist_add_selected: 'В корзину',
    wishlist_add_all:   'В корзину',
    wishlist_buy_selected: 'Купить',
    wishlist_buy_all:   'Купить всё',
    wishlist_share:     'Поделиться',
    wishlist_share_copied: 'Ссылка скопирована!',
    wishlist_share_login:  'Войдите, чтобы поделиться вишлистом',
    shared_wl_by:          'Вишлист пользователя',
    shared_wl_empty:       'Вишлист пуст',
    wishlist_clear:     'Очистить',
    wishlist_clear_confirm: 'Очистить весь список желаний?',
    wishlist_none_for_filter: 'Нет игр по этому фильтру',
    wishlist_filter_all: 'Все',
    wishlist_filter_games: 'Игры',
    wishlist_filter_dlc: 'DLC',
    wishlist_filter_sale: 'Акционные',
    wishlist_sort_date: 'По дате',
    wishlist_sort_cheap: 'Дешевле',
    wishlist_sort_expensive: 'Дороже',
    wishlist_sort_sale: 'Со скидкой',
    wishlist_sort_name: 'По названию',
    wishlist_added:     'Добавлено',
    wishlist_info_title: 'Полезная информация',
    wishlist_info_1:    'Гарантия и поддержка 30 дней',
    wishlist_info_2:    'Мгновенная доставка ключей на email',
    wishlist_info_3:    'Лучшие региональные цены',
    wishlist_info_4:    'Цены обновляются ежедневно',
    wishlist_last_day:  'Последний день',
    wishlist_period:    'Период:',
    wishlist_method:    'Способ:',
    wishlist_region:    'Регион:',
    wishlist_delete:    'Удалить',
    wishlist_instant:   'Мгновенная доставка',
    wishlist_summary:   'Итого',
    wishlist_selected:  'Выбрано',
    wishlist_more:      '+ ещё',
    wishlist_price_note: 'Цены с учётом скидок',
    login_title:        'Войти / Зарегистрироваться',
    login_otp_tab:      'Код на email',
    login_pw_tab:       'Пароль',
    login_email_label:  'EMAIL',
    login_get_code:     'Получить код',
    login_code_sent:    'Код отправлен на',
    login_enter_code:   'Введите код',
    login_resend_wait:  'Повторно через',
    login_resend:       'Отправить новый код',
    login_change_email: 'Изменить email',
    login_success:      'Вход выполнен',
    login_redirecting:  'Перенаправляем...',
    login_tab_login:    'Вход',
    login_tab_register: 'Регистрация',
    login_pw_label:     'ПАРОЛЬ',
    login_pw_placeholder: 'Минимум 8 символов',
    login_pw_confirm_label: 'ПОДТВЕРДИТЬ ПАРОЛЬ',
    login_pw_confirm_placeholder: 'Повторите пароль',
    login_register_btn: 'Зарегистрироваться',
    login_tos:          'Входя, вы соглашаетесь с условиями использования',
    login_email_placeholder: 'example@email.com',
    login_email_hint:   'Введите email — отправим код подтверждения',
    account_orders:     'Заказы',
    account_rentals:    'Аренды',
    account_wishlist:   'Желаемое',
    profile_tab_reviews: 'Отзывы',
    profile_since: 'С',
    acc_client_since: 'Клиент с',
    acc_orders_word: 'заказов',
    acc_spent_word: 'потрачено',
    acc_public_profile: 'Профиль публичный',
    acc_logout: 'Выйти',
    set_profile: 'Профиль',
    set_lang: 'Язык интерфейса',
    set_save_profile: 'Сохранить профиль',
    set_saving: 'Сохранение...',
    set_privacy: 'Приватность',
    set_public_profile: 'Публичный профиль',
    set_public_desc: 'Другие видят вашу страницу на сайте',
    set_what_show: 'ЧТО ПОКАЗЫВАТЬ В ПРОФИЛЕ',
    set_show_reviews: 'Отзывы',
    set_show_reviews_desc: 'Список ваших отзывов',
    set_show_purchases: 'Покупки',
    set_show_purchases_desc: 'Библиотека купленных игр',
    set_show_wishlist: 'Список желаемого',
    set_show_wishlist_desc: 'Ваш вишлист',
    set_show_contacts: 'Контакты',
    set_show_contacts_desc: 'Telegram / WhatsApp (из раздела Информация)',
    set_marketing: 'Маркетинговые рассылки',
    set_marketing_desc: 'Акции и скидки на почту',
    set_save: 'Сохранить',
    set_security: 'Безопасность',
    set_no_password: 'Пароль не установлен — входите через OTP',
    set_current_pw: 'Текущий пароль',
    set_new_pw: 'Новый пароль',
    set_set_pw: 'Установить пароль',
    set_change_pw: 'Изменить пароль',
    set_min8: 'Минимум 8 символов',
    set_confirm_pw: 'Подтверждение пароля',
    set_pw_mismatch: 'Пароли не совпадают',
    set_pw_min_err: 'Нужно минимум 8 символов',
    set_name_ph: 'Как вас зовут?',
    set_add_photo: 'Добавить фото',
    set_change_del_photo: 'Изменить или удалить фото',
    set_error: 'Ошибка',
    set_saved: 'Сохранено',
    set_network_error: 'Сетевая ошибка',
    profile_tab_purchases: 'Покупки',
    profile_tab_wishlist: 'Желаемое',
    account_support:    'Поддержка',
    account_info:       'Информация',
    account_settings:   'Настройки',
    account_tab_all:    'Все',
    account_tab_buy:    'Покупки',
    account_tab_rent:   'Аренда',
    account_status_all:       'Все статусы',
    account_status_pending:   'Ожидают оплаты',
    account_status_completed: 'Выполненные',
    account_status_cancelled: 'Отменённые',
    order_pay_now:            'Перейти к оплате',
    order_verifying:          'Проверяем вашу оплату',
    order_pay_other:          'Оплата не прошла? Выбрать другой способ',
    order_open_full:          'Открыть заказ',
    order_choose_method:      'Выберите способ оплаты',
    account_no_orders:  'Заказов ещё нет',
    account_no_orders_desc: 'Сделайте первый заказ в каталоге',
    account_go_catalog: 'В каталог',
    account_no_orders_filter: 'Нет заказов в этой категории',
    account_ms_account: 'Microsoft аккаунт',
    account_ms_email:   'Microsoft Email',
    account_ms_note:    'Будет использоваться автоматически при следующих покупках',
    account_contacts:   'Контакты',
    account_saving:     'Сохранение...',
    account_saved:      'Сохранено',
    account_save_contacts: 'Сохранить контакты',
    account_net_error:  'Сетевая ошибка',
    account_tg_placeholder: 'username без @',
    account_wa_placeholder: '+380...',
    releases_title:     'РЕЛИЗЫ',
    releases_desc:      'Обновляется каждый день',
    order_paid_title:        'Оплата подтверждена',
    order_step_placed:       'Заказ',
    order_step_paid:         'Оплата',
    order_step_delivered:    'Выдача',
    order_step_preparing:    'Подготовка',
    order_step_done:         'Готово',
    order_step_status_done:    'Завершено',
    order_step_status_failed:  'Не удалось',
    order_step_status_waiting: 'Ожидает оплаты',
    order_step_status_active:  'В обработке',
    order_step_status_pending: 'Ожидает',
    order_receipt:           'Чек заказа',
    order_date_label:        'Дата',
    order_total_label:       'Итого',
    order_failed_title:      'Оплата не прошла',
    order_partial_title:     'Частичная оплата',
    order_waiting_title:     'Ожидание оплаты',
    order_paid_desc:         'Менеджер пришлёт данные доступа на email в течение 1–24 ч.',
    order_done_title:        'Заказ выполнен',
    order_done_desc:         'Спасибо за покупку!',
    order_failed_desc:       'Транзакция отменена или истёк срок.',
    order_partial_desc:      'Получена неполная сумма. Доплатите остаток на тот же адрес.',
    order_waiting_desc:      'Проверяем транзакцию. Страница обновляется автоматически.',
    order_auto_refresh:      'Автообновление каждые 5 сек',
    order_not_found:         'Заказ не найден или у вас нет доступа',
    order_pay_heleket:       'Доплатить на Heleket',
    order_partial_banner:    'Частичная оплата — ожидается доплата',
    order_received_label:    'ПОЛУЧЕНО',
    order_remaining_label:   'ОСТАЛОСЬ',
    order_received_info:     'Получено:',
    order_network_label:     'сеть:',
    order_number_label:      'ЗАКАЗ',
    order_amount_label:      'СУММА',
    order_completed:         'Завершён',
    order_account_note:      'Менеджер пришлёт логин+пароль аккаунта после подтверждения оплаты.',
    order_rental_until:      'до',
    order_key_note:          'Ключ активации отправляется на email после подтверждения оплаты.',
    order_all_keys:          'Мои заказы',
    order_back_to_orders:    'К заказам',
    order_page_title:        'Заказ',
    order_badge_done:        'Выполнено',
    order_badge_paid:        'Оплачено',
    order_badge_failed:      'Оплата не прошла',
    order_badge_cancelled:   'Отменено',
    order_badge_pending:     'Ожидает оплаты',
    order_items_title:       'Товары в заказе',
    order_items_label:       'Товары',
    order_fee_label:         'Комиссия платёжной системы',
    order_details_title:     'Детали заказа',
    order_recipient_email_label: 'Email получателя',
    order_method_label:      'Способ оплаты',
    order_method_pending:    'Ещё не выбран',
    order_method_paid_fallback: 'Оплачено',
    order_method_heleket_tagline: 'крипта/карты',
    order_countdown_label: 'Оплатите в течение',
    order_countdown_expired_label: 'Время истекло',
    order_status_label:      'Статус',
    order_support_title:     'Нужна помощь?',
    order_support_desc:      'Если возникли вопросы по заказу — напишите нам.',
    order_support_cta:       'Связаться с поддержкой',
    order_fulfillment_ask_email_title: 'Укажите Microsoft-аккаунт',
    order_fulfillment_ask_email_desc:  'На эту почту мы отправим приглашение в семейную группу Xbox — одно на весь заказ.',
    order_fulfillment_submit_email:    'Продолжить',
    order_fulfillment_email_invalid:   'Введите корректный email',
    order_fulfillment_invite_title:    'Приглашение готово',
    order_fulfillment_invite_desc:     'Примите приглашение в семейную группу Xbox, затем нажмите «Я вступил».',
    order_fulfillment_family_check_warning: 'Прежде чем вступать — проверьте на account.microsoft.com/family, что вы ещё НЕ состоите в другой семейной группе Xbox (одновременно можно быть только в одной). Если состоите — сначала выйдите оттуда.',
    order_fulfillment_family_delivered_note: 'Игра выдана — она уже в вашей библиотеке (Личные игры). Мы вышли из семейной группы — это не влияет на игру.',
    order_fulfillment_open_invite:     'Открыть приглашение',
    order_fulfillment_confirm_joined:  'Я вступил',
    order_fulfillment_ownership_error: 'Не удалось подтвердить. Напишите в поддержку — мы проверим вручную.',
    order_fulfillment_change_email:    'изменить',
    order_fulfillment_save_email:      'Сохранить',
    order_fulfillment_in_progress:     'Обрабатываем выдачу',
    order_fulfillment_in_progress_desc: 'Это занимает некоторое время — страница обновится автоматически.',
    order_fulfillment_needs_attention: 'Требуется внимание оператора',
    order_fulfillment_support_hint:    'Мы уже видим эту ситуацию и занимаемся вручную — напишите в поддержку, если хотите детали.',
    order_pay_action_title:  'Оплатите заказ',
    order_pay_action_desc:   'Мы зарезервировали товары. После оплаты начнём обработку заказа.',
    order_partial_instruction: 'Heleket получил вашу транзакцию, но сумма не совпала. Доплатите остаток на тот же адрес через Heleket.',
    nav_my_orders:           'Мои заказы',
    date_today:         'Сегодня',
    date_yesterday:     'Вчера',
    date_days_ago:      'дня назад',
    date_week_ago:      '1 неделю назад',
    date_weeks_ago:     'недели назад',
    date_sale_ends_d:   'до конца акции',
    date_sale_ends_h:   'ч до конца',
    date_sale_hours:    'ч',
    date_sale_days:     'д',
    date_sale_min:      'мин',
    sort_popular:       'Популярное',
    filter_all_genres:  'Все жанры',
    filter_close:       'закрыть',
    genre_platformer:   'Платформер',
    genre_family:       'Семейные',
    genre_music:        'Музыка',
    catalog_items_count: 'товаров',
    catalog_optimized:  'Оптимизировано для Series X|S',
    preorder_title:     'Ожидаются',
    preorder_more:      'Подробнее',
    preorder_search:    'Поиск по названию...',
    preorder_all:       'Все',
    preorder_no_results:'Ничего не найдено',
    sort_date_asc:      'Дата ↑',
    sort_date_desc:     'Дата ↓',
    sort_price_asc:     'Цена ↑',
    sort_price_desc:    'Цена ↓',
    sort_rating_desc:   'Рейтинг ↓',
    sort_name_asc:      'Название ↑',
    filter_platform:    'Платформа',
    filter_genre:       'Жанр',
    filter_price:       'Цена',
    filter_features:    'Особенности',
    filter_reset:       'Сбросить',
    filter_studio:      'Студия...',
    filter_lang:        'Язык',
    filter_region:      'Регион',
    filter_gold:        'Gold Required',
    genre_action:       'Экшн',
    genre_shooter:      'Шутер',
    genre_rpg:          'RPG',
    genre_racing:       'Гонки',
    genre_sports:       'Спорт',
    genre_fighting:     'Файтинг',
    genre_simulation:   'Симулятор',
    genre_strategy:     'Стратегия',
    genre_puzzle:       'Головоломка',
    genre_horror:       'Хоррор',
    genre_adventure:    'Приключения',
    platform_all:       'Все',
    filter_genres:      'Жанры',
    filter_aria_genre:  'Фильтр по жанру',
    filter_features_reset: 'Сбросить особенности',
    filter_year_reset:  'Сбросить годы',
    filter_feat_suffix: 'особ.',
    filter_year_suffix: 'гг.',
    catalog_showing:    'Показано',
    catalog_of:         'из',
    catalog_limit_hint: 'уточните фильтры',
    delivery_key:       'Ключ',
    delivery_gift:      'Подарок',
    delivery_account:   'Аккаунт',
    delivery_rental:    'Аренда',
    cart_title:         'Корзина',
    checkout_title:        'Оформление заказа',
    checkout_back:         'Назад в корзину',
    checkout_recipient:    'Email получателя',
    checkout_recipient_hint:'Сюда отправим ключ / данные после оплаты',
    checkout_use_account_email: 'Использовать email моего аккаунта',
    checkout_email_invalid:'Укажите корректный email',
    checkout_method:       'Способ оплаты',
    checkout_no_methods:   'Нет доступных методов оплаты',
    checkout_fee:          'Комиссия метода',
    checkout_items:        'Товаров',
    checkout_total:        'Итого',
    checkout_pay:          'Оплатить',
    checkout_creating:     'Создание счёта…',
    checkout_delivery_title:'Доставка после оплаты',
    checkout_delivery_note:'Отправим данные на ваш email в течение 1–24 ч.',
    checkout_delivery_note_rental:'Отправим данные аккаунта (логин+пароль) в течение 1–24 ч.',
    checkout_secure:       'Защищённая транзакция',
    checkout_rental_duration:'Срок аренды',
    checkout_failed_title: 'Оплата не прошла',
    checkout_failed_desc:  'Средства не списаны. Попробуйте ещё раз или выберите другой способ оплаты.',
    checkout_failed_retry: 'Вернуться в корзину',
    checkout_failed_orders:'Мои заказы',
    cart_empty:         'Корзина пуста',
    cart_total:         'Итого',
    cart_checkout:      'Оформить заказ',
    cart_added:         'Добавлено',
    cart_method:        'Метод',
    cart_days:          'дн',
    cart_remove:        'Удалить',
    order_status_pending:    'Ожидает оплаты',
    order_status_paid:       'Оплачено',
    order_status_failed:     'Не оплачено',
    order_status_refunded:   'Возврат',
    fulfill_pending:         'Ожидается',
    fulfill_processing:      'В обработке',
    fulfill_partial:         'Частично',
    fulfill_delivered:       'Доставлено',
    fulfill_failed:          'Ошибка',
    key_show:                'Показать',
    key_hide:                'Скрыть ключ',
    key_copy:                'Скопировать',
    order_account_login:    'Логин',
    order_account_password: 'Пароль',
    key_copied:              'Скопировано',
    key_waiting:             'ожидаем...',
    key_open_game:           'Открыть страницу игры',
    rental_days_suffix:      'дн.',
    review_leave:       'Оставить отзыв',
    review_edit:        'Редактирование отзыва',
    review_edit_btn:    'Редактировать',
    review_rating:      'Оценка',
    review_placeholder: 'Расскажите о впечатлениях (необязательно)...',
    review_save:        'Опубликовать',
    review_update:      'Обновить',
    review_saving:      'Сохранение...',
    review_cancel:      'Отмена',
    review_thanks:      'Спасибо за отзыв!',
    review_updated:     'Отзыв обновлён!',
    review_loading:     'Загрузка...',
    review_delete_confirm: 'Удалить ваш отзыв?',
    review_label:       'Отзыв',
    rating_1: 'Плохо',  rating_2: 'Не очень', rating_3: 'Нормально',
    rating_4: 'Хорошо', rating_5: 'Отлично',
    support_title:      'Поддержка',
    support_subtitle:   'Ваши обращения в службу поддержки',
    support_new:        'Новое обращение',
    support_empty:      'Обращений нет',
    support_empty_desc: 'Здесь появятся ваши обращения. Есть вопросы — напишите нам.',
    ticket_open:        'Открыто',
    ticket_in_progress: 'В обработке',
    ticket_resolved:    'Решено',
    ticket_closed:      'Закрыто',
    ticket_subject:     'Тема',
    ticket_body:        'Сообщение',
    ticket_placeholder_subject: 'Кратко опишите проблему',
    ticket_placeholder_body:    'Подробно опишите вашу проблему...',
    ticket_submit:      'Отправить',
    ticket_submitting:  'Отправка...',
    ticket_cancel:      'Отмена',
    ticket_new_reply:   'Новый ответ поддержки',
    ticket_order:       'Заказ',
    support_load_error: 'Не удалось загрузить.',
    support_retry:      'Попробовать снова',
    td_all_tickets:     'Все обращения',
    td_load_error:      'Не удалось загрузить обращение.',
    td_await:           'Ожидаем ответа от поддержки',
    td_support:         'Поддержка',
    td_you:             'Вы',
    td_placeholder:     'Написать сообщение... (Ctrl+Enter для отправки)',
    td_send:            'Отправить',
    td_closed:          'Обращение закрыто.',
    td_order_details:   'Детали заказа',
    td_goto_orders:     'Перейти к заказам',
    td_transaction:     'Транзакция',
    td_send_error:      'Ошибка отправки.',
    td_send_error_retry:'Ошибка отправки. Попробуйте ещё раз.',
    login_error_rate:   'Слишком много попыток. Подождите 10 минут.',
    login_error_send:   'Ошибка отправки.',
    login_error_code:   'Неверный код.',
    login_error_fields: 'Заполните все поля.',
    login_error_login:  'Ошибка входа.',
    login_error_min_pw: 'Пароль минимум 8 символов.',
    login_error_pw_match: 'Пароли не совпадают.',
    login_error_reg:    'Ошибка регистрации.',
    common_loading:     'Загрузка...',
    common_error:       'Ошибка',
    common_retry:       'Попробовать снова',
    common_more:        'Ещё',
    common_close:       'Закрыть',
    common_save:        'Сохранить',
    common_cancel:      'Отмена',
    common_confirm:     'Подтвердить',
    common_see_all:     'Смотреть все',
    common_load_more:   'Загрузить ещё',
    common_not_found:   'Ничего не найдено',
    nf_title:           'Страница не найдена',
    nf_desc:            'Похоже, эта страница затерялась в космосе. Проверьте адрес или вернитесь в каталог.',
    nf_home:            'На главную',
    nf_catalog:         'В каталог',
    err_title:          'Что-то пошло не так',
    err_desc:           'Произошла непредвиденная ошибка. Попробуйте ещё раз — обычно это помогает.',
    err_retry:          'Попробовать снова',
    common_of:          'из',
    common_all:         'Все',
    common_reset_filters: 'Сбросить фильтры',
    common_remaining:   'осталось',
    sort_aria:          'Сортировка',
    common_game_count_1: 'игра',
    common_game_count_2_4: 'игры',
    common_game_count_many: 'игр',
    studio_dev:         'разработчик',
    studio_pub:         'издатель',
    studio_both:        'dev+pub',

    // -- PDP extra ------------------------------------------------------------─
    pdp_added:          'Добавлено!',
    pdp_activation_region: 'Регион активации',
    pdp_selected_region:   'Выбранный регион',
    pdp_regions_count:     'регионов',
    pdp_vpn_note:          'VPN может понадобиться для активации. Инструкция предоставляется после покупки.',
    pdp_delivery_instant:  'В наличии — Мгновенно',
    pdp_delivery_24h:      'До 24 ч',
    pdp_delivery_in:       'Есть в:',
    pdp_footer_buy:        'Защищённая оплата · Мгновенная доставка · Гарантия',
    pdp_price_label:       'Цена',
    pdp_unavailable:       'Недоступно',
    pdp_unavailable_content: 'Этот контент сейчас недоступен для покупки.',
    pdp_free_game_note:    'Эта игра бесплатная. Покупка через наш магазин недоступна.',
    pdp_popular:           'Популярное',
    pdp_rental_duration:   'Длительность аренды',
    pdp_rental_access:     'Полный доступ к аккаунту и игре',
    pdp_per_day:           '/день',
    pdp_rental_for:        'за',
    pdp_rental_days_long:  'дней',
    pdp_footer_rent:       'Безопасная аренда · Гарантия доступа · Поддержка',
    pdp_sale_active:       'Акция ещё идёт:',
    pdp_in_bundle:         'В этом бандле',
    pdp_included_in:       'Входит в',
    pdp_addons_for:        'Дополнения к игре',
    pdp_compare_editions:  'Сравнить издания',
    pdp_works_with:        'Работает с',
    pdp_people_like:       'Похожие игры',
    pdp_capabilities:      'Возможности',
    pdp_tech_info:         'Техническая информация',
    pdp_lang_support:      'Поддержка языков',
    pdp_lang_count:        'языков',
    pdp_lang_col_lang:     'Язык',
    pdp_lang_col_audio:    'Звук',
    pdp_lang_col_subs:     'Субт.',
    pdp_tech_developer:    'Разработчик',
    pdp_tech_publisher:    'Издатель',
    pdp_tech_release_year: 'Год выхода',
    pdp_tech_release_date: 'Дата выхода',
    pdp_coming_soon:       'Скоро',
    pdp_soon:              'Скоро',
    pdp_coming_soon_date:  'Релиз {date}',
    pdp_coming_soon_desc:  'Игра ещё не вышла. Цена и предзаказ появятся ближе к релизу.',
    pdp_tech_size:         'Размер',
    pdp_trust_guarantee:   'Гарантия',
    pdp_trust_support:     'Поддержка',
    pdp_trust_sold:        'Продано',

    // -- Offer filter ----------------------------------------------------------
    offer_key:          'Ключ',
    offer_gift:         'Гифт',
    offer_account:      'Аккаунт',
    offer_rental:       'Аренда',
    offer_type_title:   'Тип доставки',
    offer_region_any:   'Любой',
    offer_na:           'н/д',
    offer_unavail_for:  'Недоступно для',

    // -- Language filter ------------------------------------------------------
    filter_lang_interface: 'Интерфейс',
    filter_lang_audio:  'Аудио',
    filter_lang_subs:   'Субтитры',
    filter_lang_reset:  'Сбросить язык',

    // -- Preorder hero --------------------------------------------------------
    preorder_next_release: 'СЛЕДУЮЩИЙ РЕЛИЗ',
    preorder_preorder:  'Предзаказать',
    preorder_empty_title: 'Предзаказов нет',
    preorder_empty_desc: 'Появятся здесь автоматически',

    // -- Countdown ------------------------------------------------------------
    cd_days:            'дн',
    cd_hours:           'ч',
    cd_minutes:         'мин',
    cd_seconds:         'сек',
    cd_released:        'Уже вышел',

    // -- Sale page ------------------------------------------------------------
    sale_title:         'Скидки',
    sale_games_count:   'игр со скидкой',
    sale_updated_daily: 'обновляется ежедневно',
  },

  en: {
    nav_catalog:        'Catalog',
    nav_sales:          'Sales',
    nav_new:            'New',
    nav_preorder:       'Pre-orders',
    nav_more:           'Gift Cards',

    // -- Gift Cards ----------------------------------------------------------
    gc_related:         'Related products',
    gc_from:            'from',
    gc_region_label:    'Activation region',
    gc_region_selected: 'Selected region',
    gc_denomination:    'Denomination',
    gc_out:             'out',
    gc_add_cart:        'Add to cart',
    gc_buy_now:         'Buy now',
    gc_feat_instant:    'Instant code delivery by email',
    gc_feat_region:     'Activation in region',
    gc_feat_digital:    'Digital code',
    gc_feat_pay:        'Pay with crypto & card',
    gc_footer:          'Secure payment · Instant delivery · Guarantee',
    gc_tab_about:       'About',
    gc_tab_redeem:      'How to redeem',
    gc_tab_terms:       'Terms',
    gc_tab_reviews:     'Reviews',
    gc_no_reviews:      'No reviews yet. Be the first!',
    gc_toast_oos:       'Out of stock',
    gc_toast_cart_err:  'Cart error',
    gc_toast_added:     'Added to cart',
    gc_type_voucher:    'Voucher',
    gc_type_topup:      'Direct top-up',
    gc_type_esim:       'eSIM',
    gc_activates_in:    'Activates in countries',
    gc_subtitle:        'Gift cards, vouchers, top-ups and eSIM — Steam, PlayStation, Apple, Amazon and more.',
    gc_search_ph:       'Search…',
    gc_filters:         'Filters',
    gc_sort_title:      'By name',
    gc_sort_price:      'Cheapest',
    gc_found:           'Found',
    gc_all:             'All',
    gc_nothing:         'Nothing found.',
    gc_type_filter:     'Product type',
    gc_country_filter:  'Country / region',
    gc_sort_label:      'Sorting',
    gc_all_types:       'All types',
    gc_all_countries:   'All countries',
    gc_reset:           'Reset',
    gc_show:            'Show',
    gc_types_voucher:   'Vouchers',
    gc_types_topup:     'Top-ups',
    gc_about_body:      'a digital gift card with instant code delivery right after payment is confirmed. Choose a region and denomination, add to cart or click "Buy now". The code arrives by email and in your account.',
    gc_redeem_body:     '1. Copy the received code.\n2. Sign in to your service account.\n3. Open "Redeem code" and enter the code.\n4. The balance is credited instantly. The card works only in the selected region.',
    gc_terms_body:      'The code is valid only for accounts in the selected region. Returns after the code is revealed are not possible. Make sure your account region matches the card region.',
    search_placeholder: 'Search games, DLC...',
    aria_search:        'Search',
    aria_search_clear:  'Clear',
    aria_theme:         'Theme',
    aria_lang:          'Language',
    aria_account:       'My account',
    aria_login:         'Sign in',
    aria_menu:          'Menu',
    theme_light:        'Light theme',
    theme_dark:         'Dark theme',
    nav_aria_label:     'Navigation',
    footer_all_games:   'All games',
    footer_all_addons:  'All add-ons',
    footer_dlc:         'DLC',
    footer_account:     'Account',
    footer_login:       'Sign in',
    footer_cart:        'Cart',
    footer_wishlist:    'Wishlist',
    footer_support:     'Support',
    footer_faq:         'FAQ',
    footer_contacts:    'Contact',
    footer_terms:       'Terms',
    footer_tagline:     'Best prices on games and DLC for Xbox and PC.',
    footer_copyright:   '© 2026 GamesSales. Not an official Microsoft store.',
    section_top_sales:  'Top deals',
    section_preorder:   'Pre-orders',
    section_new:        'New arrivals',
    hero_more:          'Learn more',
    hero_prev:          'Previous',
    hero_next:          'Next',
    hero_featured:      'Featured games',
    hero_wishlist_add:  'Wishlist',
    hero_wishlist_remove: 'Remove from wishlist',
    card_game:          'GAME',
    card_dlc:           'DLC',
    pdp_about:          'About the game',
    pdp_collapse:       'Collapse',
    pdp_expand:         'Show more',
    pdp_wishlist_add:   'Wishlist',
    pdp_wishlist_remove: 'Remove from wishlist',
    pdp_buy:            'Buy',
    pdp_region:         'Region',
    pdp_delivery:       'Delivery type',
    pdp_buy_key:        'Buy key',
    pdp_buy_gift:       'Buy as gift',
    pdp_buy_account:    'Get account',
    pdp_buy_rent:       'Rent',
    pdp_rental_tab:     'Rental',
    pdp_sale_ended:     'Ended',
    pdp_select_region:  'Select region',
    pdp_no_price:       'Price on request',
    pdp_not_found:      'Product not found',
    pdp_reviews_later:  'Reviews will appear after launch',
    pdp_no_reviews:     'No reviews yet',
    pdp_reviews_buy:    'Buy the game and leave a review in',
    pdp_reviews_orders: 'My Orders',
    reviews_page_title: 'Customer Reviews',
    reviews_page_sub:   'Real impressions from games bought on GamesSales',
    pdp_tab_details:    'Details',
    pdp_tab_more:       'More',
    pdp_tab_reviews:    'Reviews',
    feat_key_1:         'Instant email delivery',
    feat_key_2:         'Activate on Xbox / PC',
    feat_key_3:         'No region restrictions',
    feat_gift_1:        'Beautiful gift wrapping',
    feat_gift_2:        'Personal message',
    feat_gift_3:        'Send to any email',
    feat_account_1:     'Ready Xbox account',
    feat_account_2:     'Game already activated',
    feat_account_3:     'Full support 30 days',
    feat_rental_1:      'Full access to game and account',
    feat_rental_2:      'Game progress is saved',
    feat_rental_3:      '24/7 support during rental',
    catalog_no_results: 'Nothing found for the selected filters',
    catalog_showing_limit: 'Refine filters — showing maximum results',
    catalog_type_game:  'Games',
    catalog_type_dlc:   'DLC',
    catalog_type_all:   'All',
    catalog_discount_label: 'Discount',
    catalog_reset_active: '× Reset',
    m_filters:  'Filters',
    m_sort:     'Sorting',
    m_show:     'Show',
    m_reset:    'Reset',
    m_more:     'More',
    m_offer:    'Delivery type',
    m_region:   'Region',
    m_lang:     'Language',
    m_caps:     'Features',
    m_special:  'Special',
    m_quick:    'Quick filters',
    filter_year:        'Year',
    filter_type:        'Type',
    filter_sort:        'Sort',
    filter_rating:      'Rating',
    filter_discount:    'Discounts',
    sort_discount_desc: 'Discount ↓',
    sort_discount_asc:  'Discount ↑',
    sort_rating_asc:    'Rating ↑',
    sort_new:           'Newest',
    cart_clear:         'Clear cart',
    cart_subtotal:      'Subtotal',
    cart_savings:       'Savings',
    cart_shipping:      'Shipping',
    cart_shipping_free: 'Free',
    cart_delivery_instant: 'Instant',
    cart_promo:         'Promo code',
    cart_promo_apply:   'Apply',
    cart_order_title:   'Your order',
    cart_benefits:      'Benefits',
    cart_benefit_1:     'Money-back guarantee',
    cart_benefit_2:     'Instant key delivery 24/7',
    cart_benefit_3:     'Best prices by region',
    cart_benefit_4:     '2800+ verified transactions',
    cart_review_note:   'Review your order before paying',
    cart_empty_desc:    'Add games from the catalog or wishlist',
    cart_go_catalog:    'Game catalog',
    cart_go_wishlist:   'Wishlist',
    cart_storage_note:  'Cart is stored locally in your browser',
    cart_to_wishlist:   'To wishlist',
    cart_warranty:      'Warranty',
    cart_feat_rental_save: 'Save vs buying',
    cart_period: 'Period',
    cart_vs_buy: 'vs buy',
    cart_note_base: 'Base game required (sold separately)',
    cart_note_sub: 'Console online requires Game Pass',
    cart_guarantee:     'Guarantee',
    cart_prices_note:   'Prices excl. VAT',
    wishlist_title:     'Wishlist',
    wishlist_desc:      'Saved games and DLC to buy',
    wishlist_empty:     'List is empty',
    wishlist_empty_desc:'Add games and DLC so you don\'t miss great deals',
    wishlist_view_catalog: 'Browse catalog',
    wishlist_select_all: 'Select all',
    wishlist_add_selected: 'To cart',
    wishlist_add_all:   'To cart',
    wishlist_buy_selected: 'Buy',
    wishlist_buy_all:   'Buy all',
    wishlist_share:     'Share',
    wishlist_share_copied: 'Link copied!',
    wishlist_share_login:  'Sign in to share your wishlist',
    shared_wl_by:          "Wishlist by",
    shared_wl_empty:       'Wishlist is empty',
    wishlist_clear:     'Clear',
    wishlist_clear_confirm: 'Clear the entire wishlist?',
    wishlist_none_for_filter: 'No games for this filter',
    wishlist_filter_all: 'All',
    wishlist_filter_games: 'Games',
    wishlist_filter_dlc: 'DLC',
    wishlist_filter_sale: 'On sale',
    wishlist_sort_date: 'By date',
    wishlist_sort_cheap: 'Cheapest',
    wishlist_sort_expensive: 'Most expensive',
    wishlist_sort_sale: 'On sale',
    wishlist_sort_name: 'By name',
    wishlist_added:     'Added',
    wishlist_info_title: 'Useful info',
    wishlist_info_1:    'Guarantee & support 30 days',
    wishlist_info_2:    'Instant key delivery to email',
    wishlist_info_3:    'Best regional prices',
    wishlist_info_4:    'Prices updated daily',
    wishlist_last_day:  'Last day',
    wishlist_period:    'Period:',
    wishlist_method:    'Method:',
    wishlist_region:    'Region:',
    wishlist_delete:    'Delete',
    wishlist_instant:   'Instant delivery',
    wishlist_summary:   'Summary',
    wishlist_selected:  'Selected',
    wishlist_more:      '+ more',
    wishlist_price_note: 'Prices including discounts',
    login_title:        'Sign in / Register',
    login_otp_tab:      'Email code',
    login_pw_tab:       'Password',
    login_email_label:  'EMAIL',
    login_get_code:     'Get code',
    login_code_sent:    'Code sent to',
    login_enter_code:   'Enter code',
    login_resend_wait:  'Resend in',
    login_resend:       'Send new code',
    login_change_email: 'Change email',
    login_success:      'Signed in',
    login_redirecting:  'Redirecting...',
    login_tab_login:    'Sign in',
    login_tab_register: 'Register',
    login_pw_label:     'PASSWORD',
    login_pw_placeholder: 'Minimum 8 characters',
    login_pw_confirm_label: 'CONFIRM PASSWORD',
    login_pw_confirm_placeholder: 'Repeat password',
    login_register_btn: 'Register',
    login_tos:          'By signing in, you agree to the terms of use',
    login_email_placeholder: 'example@email.com',
    login_email_hint:   'Enter your email — we\'ll send a confirmation code',
    account_orders:     'Orders',
    account_rentals:    'Rentals',
    account_wishlist:   'Wishlist',
    profile_tab_reviews: 'Reviews',
    profile_since: 'Since',
    acc_client_since: 'Customer since',
    acc_orders_word: 'orders',
    acc_spent_word: 'spent',
    acc_public_profile: 'Public profile',
    acc_logout: 'Log out',
    set_profile: 'Profile',
    set_lang: 'Interface language',
    set_save_profile: 'Save profile',
    set_saving: 'Saving...',
    set_privacy: 'Privacy',
    set_public_profile: 'Public profile',
    set_public_desc: 'Others can see your page on the site',
    set_what_show: 'WHAT TO SHOW ON PROFILE',
    set_show_reviews: 'Reviews',
    set_show_reviews_desc: 'List of your reviews',
    set_show_purchases: 'Purchases',
    set_show_purchases_desc: 'Library of purchased games',
    set_show_wishlist: 'Wishlist',
    set_show_wishlist_desc: 'Your wishlist',
    set_show_contacts: 'Contacts',
    set_show_contacts_desc: 'Telegram / WhatsApp (from Information section)',
    set_marketing: 'Marketing emails',
    set_marketing_desc: 'Deals and discounts by email',
    set_save: 'Save',
    set_security: 'Security',
    set_no_password: 'No password set — sign in via OTP',
    set_current_pw: 'Current password',
    set_new_pw: 'New password',
    set_set_pw: 'Set password',
    set_change_pw: 'Change password',
    set_min8: 'Minimum 8 characters',
    set_confirm_pw: 'Confirm password',
    set_pw_mismatch: 'Passwords do not match',
    set_pw_min_err: 'Minimum 8 characters required',
    set_name_ph: 'What is your name?',
    set_add_photo: 'Add photo',
    set_change_del_photo: 'Change or remove photo',
    set_error: 'Error',
    set_saved: 'Saved',
    set_network_error: 'Network error',
    profile_tab_purchases: 'Purchases',
    profile_tab_wishlist: 'Wishlist',
    account_support:    'Support',
    account_info:       'Info',
    account_settings:   'Settings',
    account_tab_all:    'All',
    account_tab_buy:    'Purchases',
    account_tab_rent:   'Rental',
    account_status_all:       'All statuses',
    account_status_pending:   'Awaiting payment',
    account_status_completed: 'Completed',
    account_status_cancelled: 'Cancelled',
    order_pay_now:            'Proceed to payment',
    order_verifying:          'Verifying your payment',
    order_pay_other:          'Payment didn\'t go through? Choose another method',
    order_open_full:          'Open order',
    order_choose_method:      'Choose a payment method',
    account_no_orders:  'No orders yet',
    account_no_orders_desc: 'Make your first order in the catalog',
    account_go_catalog: 'To catalog',
    account_no_orders_filter: 'No orders in this category',
    account_ms_account: 'Microsoft account',
    account_ms_email:   'Microsoft Email',
    account_ms_note:    'Will be used automatically on future purchases',
    account_contacts:   'Contacts',
    account_saving:     'Saving...',
    account_saved:      'Saved',
    account_save_contacts: 'Save contacts',
    account_net_error:  'Network error',
    account_tg_placeholder: 'username without @',
    account_wa_placeholder: '+1...',
    releases_title:     'RELEASES',
    releases_desc:      'Updated daily',
    order_paid_title:        'Payment confirmed',
    order_step_placed:       'Order',
    order_step_paid:         'Payment',
    order_step_delivered:    'Delivery',
    order_step_preparing:    'Preparing',
    order_step_done:         'Done',
    order_step_status_done:    'Completed',
    order_step_status_failed:  'Failed',
    order_step_status_waiting: 'Awaiting payment',
    order_step_status_active:  'In progress',
    order_step_status_pending: 'Pending',
    order_receipt:           'Order receipt',
    order_date_label:        'Date',
    order_total_label:       'Total',
    order_failed_title:      'Payment failed',
    order_partial_title:     'Partial payment',
    order_waiting_title:     'Awaiting payment',
    order_paid_desc:         'The manager will send access details to your email within 1–24 h.',
    order_done_title:        'Order complete',
    order_done_desc:         'Thank you for your purchase!',
    order_failed_desc:       'Transaction cancelled or expired.',
    order_partial_desc:      'Partial amount received. Pay the remainder to the same address.',
    order_waiting_desc:      'Verifying transaction. Page refreshes automatically.',
    order_auto_refresh:      'Auto-refresh every 5 sec',
    order_not_found:         'Order not found or you don\'t have access',
    order_pay_heleket:       'Pay remainder on Heleket',
    order_partial_banner:    'Partial payment — awaiting top-up',
    order_received_label:    'RECEIVED',
    order_remaining_label:   'REMAINING',
    order_received_info:     'Received:',
    order_network_label:     'network:',
    order_number_label:      'ORDER',
    order_amount_label:      'AMOUNT',
    order_completed:         'Completed',
    order_account_note:      'Manager will send account login+password after payment is confirmed.',
    order_rental_until:      'until',
    order_key_note:          'Activation key will be sent to your email after payment confirmation.',
    order_all_keys:          'My orders',
    order_back_to_orders:    'Back to orders',
    order_page_title:        'Order',
    order_badge_done:        'Completed',
    order_badge_paid:        'Paid',
    order_badge_failed:      'Payment failed',
    order_badge_cancelled:   'Cancelled',
    order_badge_pending:     'Awaiting payment',
    order_items_title:       'Items in order',
    order_items_label:       'Items',
    order_fee_label:         'Payment processing fee',
    order_details_title:     'Order details',
    order_recipient_email_label: 'Recipient email',
    order_method_label:      'Payment method',
    order_method_pending:    'Not selected yet',
    order_method_paid_fallback: 'Paid',
    order_method_heleket_tagline: 'crypto/cards',
    order_countdown_label: 'Pay within',
    order_countdown_expired_label: 'Time expired',
    order_status_label:      'Status',
    order_support_title:     'Need help?',
    order_support_desc:      'If you have questions about your order — contact us.',
    order_support_cta:       'Contact support',
    order_fulfillment_ask_email_title: 'Enter your Microsoft account',
    order_fulfillment_ask_email_desc:  'We will send an Xbox family invite to this email — one for the whole order.',
    order_fulfillment_submit_email:    'Continue',
    order_fulfillment_email_invalid:   'Enter a valid email',
    order_fulfillment_invite_title:    'Invite ready',
    order_fulfillment_invite_desc:     'Accept the Xbox family group invite, then click "I joined".',
    order_fulfillment_family_check_warning: 'Before joining — check at account.microsoft.com/family that you are NOT already in another Xbox family group (you can only be in one at a time). If you are, leave it first.',
    order_fulfillment_family_delivered_note: 'Your game is delivered — it\'s in your library (Personal games). We left the family group — this doesn\'t affect your game.',
    order_fulfillment_open_invite:     'Open invite',
    order_fulfillment_confirm_joined:  'I joined',
    order_fulfillment_ownership_error: 'Could not confirm. Please contact support — we\'ll check it manually.',
    order_fulfillment_change_email:    'change',
    order_fulfillment_save_email:      'Save',
    order_fulfillment_in_progress:     'Processing delivery',
    order_fulfillment_in_progress_desc: 'This takes a little while — the page will update automatically.',
    order_fulfillment_needs_attention: 'Needs operator attention',
    order_fulfillment_support_hint:    'We already see this and are handling it manually — contact support if you want details.',
    order_pay_action_title:  'Pay for your order',
    order_pay_action_desc:   'We have reserved the items. Processing starts once payment is confirmed.',
    order_partial_instruction: 'Heleket received your transaction, but the amount did not match. Pay the remainder to the same address via Heleket.',
    nav_my_orders:           'My orders',
    date_today:         'Today',
    date_yesterday:     'Yesterday',
    date_days_ago:      'days ago',
    date_week_ago:      '1 week ago',
    date_weeks_ago:     'weeks ago',
    date_sale_ends_d:   'until sale ends',
    date_sale_ends_h:   'h until end',
    date_sale_hours:    'h',
    date_sale_days:     'd',
    date_sale_min:      'min',
    sort_popular:       'Popular',
    filter_all_genres:  'All genres',
    filter_close:       'close',
    genre_platformer:   'Platformer',
    genre_family:       'Family',
    genre_music:        'Music',
    catalog_items_count: 'items',
    catalog_optimized:  'Optimized for Series X|S',
    preorder_title:     'Upcoming',
    preorder_more:      'Learn more',
    preorder_search:    'Search by title...',
    preorder_all:       'All',
    preorder_no_results:'Nothing found',
    sort_date_asc:      'Date ↑',
    sort_date_desc:     'Date ↓',
    sort_price_asc:     'Price ↑',
    sort_price_desc:    'Price ↓',
    sort_rating_desc:   'Rating ↓',
    sort_name_asc:      'Name ↑',
    filter_platform:    'Platform',
    filter_genre:       'Genre',
    filter_price:       'Price',
    filter_features:    'Features',
    filter_reset:       'Reset',
    filter_studio:      'Studio...',
    filter_lang:        'Language',
    filter_region:      'Region',
    filter_gold:        'Gold Required',
    genre_action:       'Action',
    genre_shooter:      'Shooter',
    genre_rpg:          'RPG',
    genre_racing:       'Racing',
    genre_sports:       'Sports',
    genre_fighting:     'Fighting',
    genre_simulation:   'Simulation',
    genre_strategy:     'Strategy',
    genre_puzzle:       'Puzzle',
    genre_horror:       'Horror',
    genre_adventure:    'Adventure',
    platform_all:       'All',
    filter_genres:      'Genres',
    filter_aria_genre:  'Genre filter',
    filter_features_reset: 'Reset features',
    filter_year_reset:  'Reset years',
    filter_feat_suffix: 'feat.',
    filter_year_suffix: 'yr.',
    catalog_showing:    'Showing',
    catalog_of:         'of',
    catalog_limit_hint: 'refine filters',
    delivery_key:       'Key',
    delivery_gift:      'Gift',
    delivery_account:   'Account',
    delivery_rental:    'Rental',
    cart_title:         'Cart',
    checkout_title:        'Checkout',
    checkout_back:         'Back to cart',
    checkout_recipient:    'Recipient email',
    checkout_recipient_hint:'We will send the key / details here after payment',
    checkout_use_account_email: 'Use my account email',
    checkout_email_invalid:'Enter a valid email',
    checkout_method:       'Payment method',
    checkout_no_methods:   'No payment methods available',
    checkout_fee:          'Method fee',
    checkout_items:        'Items',
    checkout_total:        'Total',
    checkout_pay:          'Pay',
    checkout_creating:     'Creating invoice…',
    checkout_delivery_title:'Delivery after payment',
    checkout_delivery_note:'We will send the details to your email within 1–24 h.',
    checkout_delivery_note_rental:'We will send account details (login+password) within 1–24 h.',
    checkout_secure:       'Secure transaction',
    checkout_rental_duration:'Rental duration',
    checkout_failed_title: 'Payment failed',
    checkout_failed_desc:  'No charge was made. Please try again or choose another payment method.',
    checkout_failed_retry: 'Back to cart',
    checkout_failed_orders:'My orders',
    cart_empty:         'Your cart is empty',
    cart_total:         'Total',
    cart_checkout:      'Checkout',
    cart_added:         'Added',
    cart_method:        'Method',
    cart_days:          'd',
    cart_remove:        'Remove',
    order_status_pending:    'Awaiting payment',
    order_status_paid:       'Paid',
    order_status_failed:     'Not paid',
    order_status_refunded:   'Refunded',
    fulfill_pending:         'Pending',
    fulfill_processing:      'Processing',
    fulfill_partial:         'Partial',
    fulfill_delivered:       'Delivered',
    fulfill_failed:          'Failed',
    key_show:                'Show',
    key_hide:                'Hide key',
    key_copy:                'Copy',
    order_account_login:    'Login',
    order_account_password: 'Password',
    key_copied:              'Copied',
    key_waiting:             'pending...',
    key_open_game:           'Open game page',
    rental_days_suffix:      'd.',
    review_leave:       'Leave a review',
    review_edit:        'Edit review',
    review_edit_btn:    'Edit',
    review_rating:      'Rating',
    review_placeholder: 'Share your experience (optional)...',
    review_save:        'Publish',
    review_update:      'Update',
    review_saving:      'Saving...',
    review_cancel:      'Cancel',
    review_thanks:      'Thanks for your review!',
    review_updated:     'Review updated!',
    review_loading:     'Loading...',
    review_delete_confirm: 'Delete your review?',
    review_label:       'Review',
    rating_1: 'Bad',    rating_2: 'Not great', rating_3: 'OK',
    rating_4: 'Good',   rating_5: 'Excellent',
    support_title:      'Support',
    support_subtitle:   'Your support tickets',
    support_new:        'New ticket',
    support_empty:      'No tickets yet',
    support_empty_desc: 'Your tickets will appear here. Have a question? Write to us.',
    ticket_open:        'Open',
    ticket_in_progress: 'In progress',
    ticket_resolved:    'Resolved',
    ticket_closed:      'Closed',
    ticket_subject:     'Subject',
    ticket_body:        'Message',
    ticket_placeholder_subject: 'Briefly describe your issue',
    ticket_placeholder_body:    'Describe your issue in detail...',
    ticket_submit:      'Submit',
    ticket_submitting:  'Sending...',
    ticket_cancel:      'Cancel',
    ticket_new_reply:   'New support reply',
    ticket_order:       'Order',
    support_load_error: 'Failed to load.',
    support_retry:      'Try again',
    td_all_tickets:     'All tickets',
    td_load_error:      'Failed to load the ticket.',
    td_await:           'Waiting for a reply from support',
    td_support:         'Support',
    td_you:             'You',
    td_placeholder:     'Write a message... (Ctrl+Enter to send)',
    td_send:            'Send',
    td_closed:          'Ticket closed.',
    td_order_details:   'Order details',
    td_goto_orders:     'Go to orders',
    td_transaction:     'Transaction',
    td_send_error:      'Failed to send.',
    td_send_error_retry:'Failed to send. Please try again.',
    login_error_rate:   'Too many attempts. Wait 10 minutes.',
    login_error_send:   'Send error.',
    login_error_code:   'Invalid code.',
    login_error_fields: 'Fill in all fields.',
    login_error_login:  'Login error.',
    login_error_min_pw: 'Password must be at least 8 characters.',
    login_error_pw_match: 'Passwords do not match.',
    login_error_reg:    'Registration error.',
    common_loading:     'Loading...',
    common_error:       'Error',
    common_retry:       'Try again',
    common_more:        'More',
    common_close:       'Close',
    common_save:        'Save',
    common_cancel:      'Cancel',
    common_confirm:     'Confirm',
    common_see_all:     'See all',
    common_load_more:   'Load more',
    common_not_found:   'Nothing found',
    nf_title:           'Page not found',
    nf_desc:            'Looks like this page drifted off into space. Check the address or head back to the catalog.',
    nf_home:            'Go home',
    nf_catalog:         'Browse catalog',
    err_title:          'Something went wrong',
    err_desc:           'An unexpected error occurred. Give it another try — that usually does the trick.',
    err_retry:          'Try again',
    common_of:          'of',
    common_all:         'All',
    common_reset_filters: 'Reset filters',
    common_remaining:   'remaining',
    sort_aria:          'Sort',
    common_game_count_1: 'game',
    common_game_count_2_4: 'games',
    common_game_count_many: 'games',
    studio_dev:         'developer',
    studio_pub:         'publisher',
    studio_both:        'dev+pub',

    // -- PDP extra ------------------------------------------------------------─
    pdp_added:          'Added!',
    pdp_activation_region: 'Activation region',
    pdp_selected_region:   'Selected region',
    pdp_regions_count:     'regions',
    pdp_vpn_note:          'VPN may be required for activation. Instructions provided after purchase.',
    pdp_delivery_instant:  'In stock — Instant',
    pdp_delivery_24h:      'Up to 24h',
    pdp_delivery_in:       'Available in:',
    pdp_footer_buy:        'Secure payment · Instant delivery · Guarantee',
    pdp_price_label:       'Price',
    pdp_unavailable:       'Unavailable',
    pdp_unavailable_content: 'This content is currently unavailable for purchase.',
    pdp_free_game_note:    'This game is free-to-play. Purchase through our store is unavailable.',
    pdp_popular:           'Popular',
    pdp_rental_duration:   'Rental duration',
    pdp_rental_access:     'Full access to account and game',
    pdp_per_day:           '/day',
    pdp_rental_for:        'for',
    pdp_rental_days_long:  'days',
    pdp_footer_rent:       'Safe rental · Access guarantee · Support',
    pdp_sale_active:       'Sale ends in:',
    pdp_in_bundle:         'In this bundle',
    pdp_included_in:       'Included in',
    pdp_addons_for:        'Add-ons for this game',
    pdp_compare_editions:  'Compare editions',
    pdp_works_with:        'Works with',
    pdp_people_like:       'People also like',
    pdp_capabilities:      'Features',
    pdp_tech_info:         'Technical information',
    pdp_lang_support:      'Language support',
    pdp_lang_count:        'languages',
    pdp_lang_col_lang:     'Language',
    pdp_lang_col_audio:    'Audio',
    pdp_lang_col_subs:     'Subs.',
    pdp_tech_developer:    'Developer',
    pdp_tech_publisher:    'Publisher',
    pdp_tech_release_year: 'Release year',
    pdp_tech_release_date: 'Release date',
    pdp_coming_soon:       'Coming soon',
    pdp_soon:              'Soon',
    pdp_coming_soon_date:  'Release {date}',
    pdp_coming_soon_desc:  'This game is not out yet. Price and pre-order will appear closer to release.',
    pdp_tech_size:         'Size',
    pdp_trust_guarantee:   'Guarantee',
    pdp_trust_support:     'Support',
    pdp_trust_sold:        'Sold',

    // -- Offer filter ----------------------------------------------------------
    offer_key:          'Key',
    offer_gift:         'Gift',
    offer_account:      'Account',
    offer_rental:       'Rental',
    offer_type_title:   'Delivery type',
    offer_region_any:   'Any',
    offer_na:           'N/A',
    offer_unavail_for:  'Unavailable for',

    // -- Language filter ------------------------------------------------------
    filter_lang_interface: 'Interface',
    filter_lang_audio:  'Audio',
    filter_lang_subs:   'Subtitles',
    filter_lang_reset:  'Reset language',

    // -- Preorder hero --------------------------------------------------------
    preorder_next_release: 'NEXT RELEASE',
    preorder_preorder:  'Pre-order',
    preorder_empty_title: 'No pre-orders',
    preorder_empty_desc: 'Will appear here automatically',

    // -- Countdown ------------------------------------------------------------
    cd_days:            'd',
    cd_hours:           'h',
    cd_minutes:         'm',
    cd_seconds:         's',
    cd_released:        'Already released',

    // -- Sale page ------------------------------------------------------------
    sale_title:         'Deals',
    sale_games_count:   'games on sale',
    sale_updated_daily: 'updated daily',
  },
} as const satisfies Record<Lang, Record<string, string>>;

export type I18nKey = keyof typeof dict.uk;

const DEFAULT_LANG: Lang = 'uk';

export function t(key: I18nKey, lang: string | null | undefined): string {
  const l = (lang ?? DEFAULT_LANG) as Lang;
  return dict[l]?.[key] ?? dict[DEFAULT_LANG][key] ?? key;
}

/**
 * Server-side: reads lang from request cookie (Next.js App Router).
 * Falls back to DEFAULT_LANG when called outside a request context — except
 * on ru.gamessales.com, where a first-time visitor (no explicit `lang`
 * cookie yet) should immediately see the RU locale/prices rather than the
 * site-wide default (per user 2026-08-26). An explicit cookie (from the
 * language switcher) always wins, on any host.
 *
 * NOTE: ru.gamessales.com is edge-proxied through a separate RU server
 * (sprintbox) which rewrites the Host header to `gamessales.com` before
 * forwarding to origin (needed for nginx server_name routing here) — so the
 * Host header itself can't be used to detect the ru. edge. That proxy sets
 * `X-Edge-Locale: ru` instead; check that.
 */
export async function getLang(): Promise<Lang> {
  try {
    const { cookies, headers } = await import('next/headers');
    const jar = await cookies();
    const val = jar.get('lang')?.value;
    if (val === 'en' || val === 'ru' || val === 'uk') return val;

    const hdrs = await headers();
    // ПАСТКА: sprintbox-конфіг виставляє X-Edge-Locale ДВІЧІ (дублікат
    // директиви) — nginx об'єднує однойменні proxy_set_header у "ru, ru".
    if (hdrs.get('x-edge-locale')?.split(',')[0]?.trim() === 'ru') return 'ru';
  } catch {
    // outside request context (e.g. static generation without cookies)
  }
  return DEFAULT_LANG;
}

/** Plural helper for game counts: 1 гра / 2-4 гри / 5+ ігор */
export function gameCount(n: number, lang: string | null | undefined): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 19) return `${n} ${t('common_game_count_many', lang)}`;
  if (mod10 === 1) return `${n} ${t('common_game_count_1', lang)}`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} ${t('common_game_count_2_4', lang)}`;
  return `${n} ${t('common_game_count_many', lang)}`;
}
